import { ToastType } from "@/enums";
import { useDeleteWorkspaceInvite } from "@/queries/workspace-invites";
import { useCreateWorkspaceMember } from "@/queries/workspace-members";
import { WorkspaceInvite } from "@/types/workspace-invite";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { URL_PARAM_KEYS } from "@/utils/utils";
import { useState } from "react";

export function useInviteActions(sameOrgInvites: WorkspaceInvite[]) {
  const createWorkspaceMember = useCreateWorkspaceMember();
  const deleteWorkspaceInvite = useDeleteWorkspaceInvite();
  const [isBulkAccepting, setIsBulkAccepting] = useState(false);
  const [isBulkDeclining, setIsBulkDeclining] = useState(false);

  const deleteInviteSearchParam = () => {
    sessionStorage.removeItem(URL_PARAM_KEYS.INVITE);
  };

  const handleAccept = (id: number) => {
    createWorkspaceMember.mutate({ workspace_invite_id: id });
    deleteInviteSearchParam();
  };

  const handleDecline = (id: number) => {
    deleteWorkspaceInvite.mutate(id);
    deleteInviteSearchParam();
  };

  const acceptAll = async () => {
    setIsBulkAccepting(true);
    let succeeded = 0;
    let failed = 0;
    function handleSuccess(data: any) {
      if ("message" in data) {
        failed++;
      } else {
        succeeded++;
      }
    }
    for (const invite of sameOrgInvites) {
      try {
        await createWorkspaceMember.mutateAsync(
          {
            workspace_invite_id: invite.id,
          },
          {
            onSuccess: handleSuccess,
          },
        );
      } catch {
        failed++;
      }
    }
    const total = sameOrgInvites.length;

    if (succeeded === total) {
      displayToast(
        `You have successfully accepted all ${succeeded} invites.`,
        ToastType.success,
      );
    } else if (succeeded > 0) {
      displayToast(
        `You have accepted ${succeeded} invites, but ${failed} failed.`,
        ToastType.warning,
      );
    } else {
      displayErrorToast("Failed to accept all invites.");
    }

    setIsBulkAccepting(false);
    deleteInviteSearchParam();
  };

  const declineAll = async () => {
    try {
      setIsBulkDeclining(true);
      const promises = sameOrgInvites.map((invite) =>
        deleteWorkspaceInvite.mutateAsync(invite.id),
      );
      const results = await Promise.allSettled(promises);
      const fulfilledCount = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const rejectedCount = results.length - fulfilledCount;
      if (fulfilledCount === results.length) {
        displayToast(
          `You have successfully declined ${fulfilledCount} invites.`,
          ToastType.success,
        );
      } else if (fulfilledCount > 0) {
        displayToast(
          `You have declined ${fulfilledCount} invites, but ${rejectedCount} failed.`,
          ToastType.warning,
        );
      } else {
        displayErrorToast("Failed to decline all invites.");
      }
    } catch (error: any) {
      displayErrorToast(error.message || "Failed to decline all invites");
    } finally {
      setIsBulkDeclining(false);
      deleteInviteSearchParam();
    }
  };

  return {
    isAccepting: createWorkspaceMember.isLoading,
    isDeclining: deleteWorkspaceInvite.isLoading,
    handleAccept,
    handleDecline,
    acceptAll,
    declineAll,
    isBulkAccepting,
    isBulkDeclining,
  };
}
